import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  FormControlLabel,
  Link,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import {
  FormContainer,
  Title,
  Subtitle,
  CheckboxContainer,
  CheckboxLabel,
  MarketingConsentLabel,
  SubmitButtonWrapper,
  inputLabelStyle,
  inputStyle,
  checkboxStyle,
  submitButtonStyle,
  helperTextErrorStyle,
} from './ConfirmDetails.styles';

import { SpringHealthFormData } from '../../SpringHealthEnrollment';

import Button from '@/components/Button';
import TextField from '@/components/TextField';
import { Product, getProductTermsAndConditions, getProductPrivacyPolicy } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { useSpringId } from '@/hooks/useSpringId';
import useSessionTimeout from '@/hooks/useSessionTimeout';
import { useEnrollPatientPlatgen } from '@/hooks/useEnrollPatientPlatgen';

type FormFieldProps = {
  name: keyof SpringHealthFormData;
  testId: string;
  label: string;
  placeholder: string;
  required?: boolean;
  type?: string;
  InputProps?: Record<string, unknown>;
  onBlurExtra?: () => void;
};

const FormField: React.FC<FormFieldProps> = ({
  name,
  testId,
  label,
  placeholder,
  required,
  type,
  InputProps: inputPropsOverride,
  onBlurExtra,
}) => {
  const { control, formState: { errors }, clearErrors } = useFormContext<SpringHealthFormData>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          onBlur={() => {
            field.onBlur();
            onBlurExtra?.();
          }}
          data-testid={testId}
          required={required}
          label={label}
          placeholder={placeholder}
          type={type}
          helperText={errors?.[name]?.message}
          error={!!errors?.[name]}
          onFocus={() => clearErrors(name)}
          InputLabelProps={{ shrink: true, style: inputLabelStyle }}
          InputProps={{ style: inputStyle, ...inputPropsOverride }}
          FormHelperTextProps={{ style: errors?.[name] ? helperTextErrorStyle : undefined }}
        />
      )}
    />
  );
};

type Props = {
  product: Product;
  onSuccess: () => void;
  onError: () => void;
};

const ConfirmDetails: React.FC<Props> = ({ product, onSuccess, onError }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { springId, sessionId } = useSpringId();
  const { isExpired } = useSessionTimeout();
  const { enroll, loading } = useEnrollPatientPlatgen();
  const {
    formState: { isValid },
    setError,
    clearErrors,
    handleSubmit,
    getValues,
  } = useFormContext<SpringHealthFormData>();

  const program = getProgram(product);

  const onSubmit = async (data: SpringHealthFormData) => {
    if (isExpired) {
      return;
    }

    posthog?.capture('ActivationFlowSubmitted', {
      program,
      product,
      spring_id: springId,
      session_id: sessionId,
    });

    try {
      const response = await enroll({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        phone: data.phoneNumber,
        product,
        springId,
      });

      if (response.data?.enrollPatientPlatgen?.success) {
        posthog?.capture('ActivationFlowCompleted', {
          program,
          product,
          spring_id: springId,
          session_id: sessionId,
        });

        onSuccess();
        return;
      }

      onError();
    } catch (e) {
      onError();
    }
  };

  return (
    <FormContainer>
      <Title>
        {t('coverage.spring_health.confirm_details.title')}
      </Title>
      <Subtitle>
        {t('coverage.spring_health.confirm_details.subtitle')}
      </Subtitle>

      <FormField
        name="firstName"
        testId="first-name-input"
        required
        label={t('coverage.spring_health.confirm_details.f_name_label')}
        placeholder={t('coverage.spring_health.confirm_details.f_name_placeholder')}
      />

      <FormField
        name="lastName"
        testId="last-name-input"
        required
        label={t('coverage.spring_health.confirm_details.l_name_label')}
        placeholder={t('coverage.spring_health.confirm_details.l_name_placeholder')}
      />

      <FormField
        name="zipCode"
        testId="zip-code-input"
        required
        label={t('coverage.spring_health.confirm_details.zipcode_label')}
        placeholder={t('coverage.spring_health.confirm_details.zipcode_placeholder')}
      />

      <FormField
        name="email"
        testId="email-input"
        required
        label={t('coverage.spring_health.confirm_details.email_label')}
        placeholder={t('coverage.spring_health.confirm_details.email_placeholder')}
      />

      <FormField
        name="phoneNumber"
        testId="phone-input"
        label={t('coverage.spring_health.confirm_details.phone_label')}
        placeholder={t('coverage.spring_health.confirm_details.phone_placeholder')}
      />

      <FormField
        name="password"
        testId="password-input"
        required
        label={t('coverage.spring_health.confirm_details.password_label')}
        placeholder={t('coverage.spring_health.confirm_details.password_placeholder')}
        type="password"
        onBlurExtra={() => {
          setTimeout(() => {
            const confirmPwd = getValues('confirmPassword');
            if (confirmPwd) {
              if (getValues('password') !== confirmPwd) {
                setError('confirmPassword', {
                  type: 'manual',
                  message: t('coverage.spring_health.errors.passwords_must_match'),
                });
              } else {
                clearErrors('confirmPassword');
              }
            }
          }, 0);
        }}
      />

      <FormField
        name="confirmPassword"
        testId="confirm-password-input"
        required
        label={t('coverage.spring_health.confirm_details.confirm_password_label')}
        placeholder={t('coverage.spring_health.confirm_details.confirm_password_placeholder')}
        type="password"
        onBlurExtra={() => {
          setTimeout(() => {
            const confirmPwd = getValues('confirmPassword');
            if (confirmPwd && getValues('password') !== confirmPwd) {
              setError('confirmPassword', {
                type: 'manual',
                message: t('coverage.spring_health.errors.passwords_must_match'),
              });
            }
          }, 0);
        }}
      />

      <CheckboxContainer>
        <Controller
          name="acceptTerms"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <CheckboxLabel>
                  {t('coverage.spring_health.confirm_details.accept_terms_prefix')}
                  {' '}
                  <Link
                    href={getProductPrivacyPolicy(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="always"
                  >
                    {t('coverage.spring_health.confirm_details.privacy_policy')}
                  </Link>
                  {' '}
                  {t('coverage.spring_health.confirm_details.terms_and')}
                  {' '}
                  <Link
                    href={getProductTermsAndConditions(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="always"
                  >
                    {t('coverage.spring_health.confirm_details.terms_of_use')}
                  </Link>
                </CheckboxLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="accept-terms-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />

        <Controller
          name="smsConsent"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <CheckboxLabel>
                  {t('coverage.spring_health.confirm_details.sms_consent')}
                </CheckboxLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="sms-consent-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />

        <Controller
          name="marketingConsent"
          render={({ field }) => (
            <FormControlLabel
              style={{ marginBottom: 24, alignItems: 'flex-start' }}
              label={(
                <MarketingConsentLabel>
                  {t('coverage.spring_health.confirm_details.marketing_consent')}
                </MarketingConsentLabel>
              )}
              control={(
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  data-testid="marketing-consent-checkbox"
                  style={checkboxStyle}
                />
              )}
            />
          )}
        />
      </CheckboxContainer>

      <SubmitButtonWrapper>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          data-testid="submit-button"
          disabled={!isValid || loading}
          loading={loading}
          rounded
          onClick={handleSubmit(onSubmit)}
          style={{
            ...submitButtonStyle,
            backgroundColor: (!isValid || loading) ? undefined : '#111',
            color: '#FFF',
          }}
        >
          {t('coverage.spring_health.confirm_details.submit_button')}
        </Button>
      </SubmitButtonWrapper>
    </FormContainer>
  );
};

export default ConfirmDetails;
